import React, { MouseEvent, ChangeEvent } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getBellAlertIdType, getDateDifference } from 'components/bellAlerts/helpers';
import { isRNWebview } from 'constants/constants';
import { getDateTime, timeNow, toUTC } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { noop } from 'helpers/utils';

import { COMMON_BELL_ALERT, USER_BELL_ALERT } from '../constants';
import GetCategoryIcon from '../GetCategoryIcon';
import MarkAsRead from '../MarkAsRead';
import styles from './bellAlertCard.module.scss';
import { IBellAlertCardProps } from './types';

/**
 * Bell Alert Card is a component which shows Bell Alert
 * @typedef {Object} IBellAlertCardProps
 * @params {Object} details - All the details of bell Alert here is type declartion IBellAlert
 * @params {(id: number) => boolean} isRead - Tells if a bell alert is already read or not
 * @params {(details: Object) => void} handleRead - make that bell alert read which you pass in it
 * @params {boolean} stretch - is bell alert card takes full width or not
 * @params {boolean} transparent - if card have transparent background color
 * @params {boolean} displayIcon - if category icons are visible on icon
 * @params {Object} TExpandedCard expandedCard - provide details of current expanded card
 * @params {(expanded : Object) => void} setExpandedCard - setter function of expanded card
 */

const BellAlertCard = ({
  details,
  isRead,
  handleRead,
  stretch = false,
  transparent = false,
  displayIcons = true,
  expandedCard,
  setExpandedCard,
}: IBellAlertCardProps) => {
  const { t } = useTranslation(['common']);

  const currentUTCTime = toUTC(timeNow());
  const bellAlertTime = toUTC(getDateTime(details.start_date));

  const { dateDifference, timeUnit } = getDateDifference(bellAlertTime, currentUTCTime);
  const idType = getBellAlertIdType(details.is_user_bell_alert);

  const handleReadPropagation = (event: ChangeEvent<{}>, id: number) => {
    event.stopPropagation();
    handleRead(id, idType);
  };

  const handleCardClick = () => {
    let expandedCardId = null;
    let isExpandedCardUserBellAlert = false;

    // if card is of COMMON_BELL_ALERT type
    if (!details.is_user_bell_alert) {
      if (expandedCard.id !== details.id) {
        handleRead(details.id, COMMON_BELL_ALERT);
        expandedCardId = details.id;
        isExpandedCardUserBellAlert = false;
      }
    }
    // if card is of USER_BELL_ALERT type
    else if (
      expandedCard.id !== details.user_bell_alert_id &&
      details.user_bell_alert_id
    ) {
      handleRead(details.user_bell_alert_id, USER_BELL_ALERT);
      expandedCardId = details.user_bell_alert_id;
      isExpandedCardUserBellAlert = true;
    }
    setExpandedCard({
      id: expandedCardId,
      isUserBellAlert: isExpandedCardUserBellAlert,
    });
  };

  const ctaClickHandler = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    if (!details.read) {
      handleRead(details.id, idType);
    }
    trackMixpanelEvent('Bell Alerts CTA Clicked', {
      bellAlert_name: details?.meta_data?.analytic_tag,
    });
  };

  // checks if current bell alert card is expanded or not
  const isCardExpand = () => {
    if (expandedCard.id === null) {
      return false;
    }
    if (expandedCard.isUserBellAlert) {
      return expandedCard.id === details.user_bell_alert_id;
    }
    return expandedCard.id === details.id;
  };

  // returns specific id according to bell alert type
  const getSpecificBellAlertId = () => {
    let specificId;
    if (details.is_user_bell_alert && details.user_bell_alert_id) {
      specificId = details.user_bell_alert_id;
    } else {
      specificId = details.id;
    }
    return specificId;
  };

  return (
    (<div
      data-testid={`bellAlert card - ${details.id}`}
      className={cn(styles.cardContainer, {
        [styles.transparentCard]: transparent,
        [styles.changeBackground]: isCardExpand(),
        [styles.largeSizes]: stretch,
        [styles.smallSizes]: !stretch,
      })}
      onKeyDown={noop}
      onClick={handleCardClick}
      role="presentation"
      data-palette="BellAlertCard">
      <div className={styles.contentSummary}>
        {displayIcons && (
          <div className={styles.leftContainer} data-palette="BellAlertCard">
            <GetCategoryIcon categoryName={details.category} />
          </div>
        )}
        <div className={styles.rightContainer}>
          <div className={styles.upperRow}>
            <div
              className={cn({
                [styles.stretch]: stretch,
                [styles.withdraw]: !stretch,
              })}
            >
              <div className={styles.titleBox}>{details.title}</div>
              <div className={styles.timeRow}>
                {dateDifference} {timeUnit} {t('common:ago')}
              </div>
            </div>
            <MarkAsRead
              handleRead={handleReadPropagation}
              isRead={isRead}
              id={getSpecificBellAlertId()}
              idType={idType}
            />
          </div>
          {details.meta_data?.description && (
            <div
              className={cn(styles.contentRow, {
                [styles.lineClamp]: !isCardExpand(),
                [styles.contentExpand]: isCardExpand(),
              })}
              data-palette="BellAlertCard">
              {details.meta_data?.description.split('\n').map((lineContent, index) => {
                // eslint-disable-next-line react/no-array-index-key
                return <p key={index} data-palette="BellAlertCard">{lineContent}</p>;
              })}
            </div>
          )}
          {details.cta_text && details.cta_link && (
            <div className={styles.buttonRow} data-palette="BellAlertCard">
              <a
                data-testid="bell-alert-cta-button"
                href={details.cta_link}
                onClick={ctaClickHandler}
                className={styles.redirectBtn}
                target={isRNWebview() ? '_self' : '_blank'}
                rel="noopener noreferrer"
              >
                {details.cta_text}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>)
  );
};

export default BellAlertCard;
